import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useSelector } from 'react-redux';
import { SingleValue } from 'react-select';
import { useAppDispatch } from 'app/store';
import TimeZoneDifferenceBanner from 'components/timeOff/TimeZoneDifferenceBanner';
import { ISelectOption } from 'components/shared/forms/DropdownSelect/DropdownSelect';
import CustomDatePicker from 'components/shared/forms/datePickers/CustomDatePicker';
import CustomTimePicker from 'components/shared/forms/timePickers/CustomTimePicker';
import DropdownSelect from 'components/shared/forms/DropdownSelect';
import CustomCheckbox from 'components/shared/forms/CustomCheckbox';
import TextArea from 'components/shared/forms/textAreas/TextArea';
import Button from 'components/shared/Button';
import { MaximumLargeCommentLengthValidationError } from 'constants/formsConstants';
import { isLoadingTimeEntriesSelector } from 'features/timeEntries/timeEntriesSlice';
import { selectInfoUserSelector } from 'features/user/userSlice';
import {organizationInfoSelector} from "../../../features/organization/organizationSlice";
import { AddTimeFormState } from 'features/timesheets/timesheetsTypes';
import { addTimeEntry } from 'features/timeEntries/timeEntriesActions';
import {
  memberInfoTimesheetsSelector,
  setAddTimeFormState,
} from 'features/timesheets/timesheetsSlice';
import { checkIsNextDay } from 'helpers/checkIsNextDay';
import { setTimeInDate } from 'helpers/setTimeInDate';
import { checkIsFuture } from 'helpers/checkIsFuture';
import { checkIsToday } from 'helpers/checkIsToday';
import styles from './AddTimeForm.module.scss';
import {anyJiraConnections, generateJiraIssueDescription} from "features/jira/jiraActions";
import {anyJiraConnectionsSelector} from "features/jira/jiraSlice";
import {IGenerateJiraIssuesDescription} from "../../../features/jira/jiraTypes";
import ActionsPopover from 'components/shared/ActionsPopover';
import CustomInput from "../../shared/forms/CustomInput";
import {PulseLoader} from "react-spinners";

interface AddTimeFormNewProps {
  data: AddTimeFormState;
  handleClose: () => void;
}

const AddTimeForm: FC<AddTimeFormNewProps> = ({ data, handleClose }) => {
  const jiraLinkPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/\S*)?$/;
    
  const isTimeEntriesLoading = useSelector(isLoadingTimeEntriesSelector);
  const selectedMemberInfo = useSelector(memberInfoTimesheetsSelector);
  const userInfo = useSelector(selectInfoUserSelector);
  const organizationInfo = useSelector(organizationInfoSelector);
  const jiraConnectionsExist = useSelector(anyJiraConnectionsSelector);
  const dispatch = useAppDispatch();
  const [isNextDay, setIsNextDay] = useState(false);
  const [isFuture, setIsFuture] = useState(false);
  const [isProjectFieldTouched, setIsProjectFieldTouched] = useState(false);
  const [jiraIssueLinks, setJiraIssueLinks] = useState(['']);
  const [jiraIssueLinkErrors, setJiraIssueLinkErrors] = useState([false]);
  const [isJiraLoading, setIsJiraLoading] = useState(false);
  
  const isJiraAvailable = organizationInfo.permissions?.some(
    (el) => el.name === 'AccessJiraTicketBasedTimeEntryCommentGeneration'
  );
  
  const handleAddJiraIssueLink = () => {
      if (jiraIssueLinks.length < 3) {
          setJiraIssueLinks([...jiraIssueLinks, ""]);
      }
  };
  
  const handleJiraIssueLinkChange = (index: number, value: string) => {
      const newJiraIssueLinks = [...jiraIssueLinks];
      newJiraIssueLinks[index] = value;
      setJiraIssueLinks(newJiraIssueLinks);

      const updatedErrors = [...jiraIssueLinkErrors];
      updatedErrors[index] = value !== '' && !jiraLinkPattern.test(value);
      setJiraIssueLinkErrors(updatedErrors);
  };

  const isJiraGenerateButtonEnabled =
      jiraIssueLinks.some((link) => link.trim() !== '') &&
      jiraIssueLinkErrors.every((error) => !error);
  
  const isToday = checkIsToday(
    data.timeTrackingDate,
    data.selectedTimeZoneName
  );

  const handleProjectChange = (project: SingleValue<ISelectOption>) => {
    dispatch(
      setAddTimeFormState({
        ...data,
        project,
        isProjectSelected: true,
      })
    );
  };
  const handleTimeTrackingDateChange = (timeTrackingDate: Date) => {
    dispatch(
      setAddTimeFormState({
        ...data,
        timeTrackingDate,
        startTime: setTimeInDate(
          timeTrackingDate,
          data.startTime.getHours(),
          data.startTime.getMinutes()
        ),
        endTime: setTimeInDate(
          timeTrackingDate,
          isNextDay ? data.endTime.getHours() + 24 : data.endTime.getHours(),
          data.endTime.getMinutes()
        ),
      })
    );
  };

  const handleStartTimeChange = (inputStartTime: Date) => {
    if (
      // IS NEXT DAY BY TIME COMPARISON
      inputStartTime.getHours() > data.endTime.getHours() ||
      (inputStartTime.getHours() === data.endTime.getHours() &&
        inputStartTime.getMinutes() >= data.endTime.getMinutes())
    ) {
      dispatch(
        setAddTimeFormState({
          ...data,
          startTime: setTimeInDate(
            data.timeTrackingDate,
            inputStartTime.getHours(),
            inputStartTime.getMinutes()
          ),
          endTime: setTimeInDate(
            data.timeTrackingDate,
            data.endTime.getHours() + 24,
            data.endTime.getMinutes()
          ),
        })
      );
    } else {
      dispatch(
        setAddTimeFormState({
          ...data,
          startTime: setTimeInDate(
            data.timeTrackingDate,
            inputStartTime.getHours(),
            inputStartTime.getMinutes()
          ),
          endTime: setTimeInDate(
            data.timeTrackingDate,
            data.endTime.getHours(),
            data.endTime.getMinutes()
          ),
        })
      );
    }
  };
  const handleEndTimeChange = (inputEndTime: Date) => {
    if (
      // IS NEXT DAY BY TIME COMPARISON
      inputEndTime.getHours() < data.startTime.getHours() ||
      (inputEndTime.getHours() === data.startTime.getHours() &&
        inputEndTime.getMinutes() <= data.startTime.getMinutes())
    ) {
      dispatch(
        setAddTimeFormState({
          ...data,
          endTime: setTimeInDate(
            data.timeTrackingDate,
            inputEndTime.getHours() + 24,
            inputEndTime.getMinutes()
          ),
        })
      );
    } else {
      dispatch(
        setAddTimeFormState({
          ...data,
          endTime: setTimeInDate(
            data.timeTrackingDate,
            inputEndTime.getHours(),
            inputEndTime.getMinutes()
          ),
        })
      );
    }
  };
  const toggleBillable = () => {
    dispatch(setAddTimeFormState({ ...data, isBillable: !data.isBillable }));
  };
  const handleNotesChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(
      setAddTimeFormState({
        ...data,
        notes: e.target.value,
        commentsLengthError:
          e.target.value.length > MaximumLargeCommentLengthValidationError,
      })
    );
  };
  const handleSubmit = () => {
    dispatch(
      addTimeEntry({
        memberId: Number(selectedMemberInfo.id),
        projectId: Number(data.project?.value),
        start: zonedTimeToUtc(
          data.startTime,
          data.selectedTimeZoneName
        ).toISOString(),
        end: zonedTimeToUtc(
          data.endTime,
          data.selectedTimeZoneName
        ).toISOString(),
        isBillable: data.isBillable,
        note: data.notes,
      })
    );

    handleClose();
  };
  
  const handleGenerateJiraIssueSummary = () => {
      if (jiraIssueLinks) {
          setIsJiraLoading(true);
          const request: IGenerateJiraIssuesDescription = {
              urls: jiraIssueLinks,
          }
          dispatch(generateJiraIssueDescription(request))
              .then(result => {
                  console.log(result);
                  const payload = result.payload ?? "";
                  dispatch(
                          setAddTimeFormState({
                              ...data,
                              notes: payload,
                              commentsLengthError: payload.length > MaximumLargeCommentLengthValidationError,
                          })
                      );
              })
              .finally(() => {
                  setJiraIssueLinks(['']);
                  setIsJiraLoading(false);
              });
      }
  }
  
  const projectsOptions = selectedMemberInfo.projects.map((p) => ({
    value: String(p.id),
    label: p.name,
  }));
  const disableSubmitCondition =
    isFuture ||
    !data.isProjectSelected ||
    isTimeEntriesLoading ||
    data.commentsLengthError;

  useEffect(() => {
    setIsNextDay(checkIsNextDay(data.startTime, data.endTime));
  }, [data.startTime, data.endTime]);

  useEffect(() => {
    setIsFuture(checkIsFuture(data.endTime, data.selectedTimeZoneName));
  }, [data.endTime, data.selectedTimeZoneName]);

  useEffect(() => {
    if (selectedMemberInfo.projects.length === 1) {
      dispatch(
        setAddTimeFormState({
          ...data,
          project: {
            label: selectedMemberInfo.projects[0].name,
            value: String(selectedMemberInfo.projects[0].id),
          },
          isProjectSelected: true,
        })
      );
    }
    if(isJiraAvailable) {
      dispatch(anyJiraConnections());
    }
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.memberName}>{selectedMemberInfo.name}</div>
      <DropdownSelect
        label="Project*"
        value={data.project}
        options={projectsOptions}
        onChange={handleProjectChange}
        isSearchable={false}
        blurInputOnSelect
        placeholder="Select a project"
        error={!data.isProjectSelected && isProjectFieldTouched}
        errorMessage="*Project should be selected"
        onBlur={() => setIsProjectFieldTouched(true)}
      />
      {isJiraAvailable && jiraConnectionsExist &&
        jiraIssueLinks.map((value, index) => (
          <div className={styles.jiraWrapper} key={index}>
            <CustomInput
              label={index === 0 ? "Jira Issue" : ""}
              value={jiraIssueLinks[index]}
              placeholder={"https://example/browse/A-" + (index + 1)}
              onChange={(e) => handleJiraIssueLinkChange(index, e.target.value)}
              error={jiraIssueLinkErrors[index]}
              errorMessage={"Invalid format. Please enter a valid URL"}
            />
            {index === jiraIssueLinks.length - 1 && 
              <div className={styles.jiraActionWrapper}>
                <ActionsPopover>
                  <li onClick={isJiraGenerateButtonEnabled ? handleGenerateJiraIssueSummary : undefined}>
                    <button aria-label={'Generate description'} disabled={!isJiraGenerateButtonEnabled} className={styles.jiraActionButtonWrapper}>Generate</button>
                  </li>
                  <li onClick={jiraIssueLinks.length < 3 ? handleAddJiraIssueLink : undefined}>
                    <button aria-label={'Add line for url'} disabled={jiraIssueLinks.length >= 3} className={styles.jiraActionButtonWrapper}>Add line</button>
                  </li>
                </ActionsPopover>
              </div>
            }
          </div>
        ))
      }
      <div className={styles.trackerWrapper}>
        <div className={styles.dateWrapper}>
          <CustomDatePicker
            now={data.calendarNow}
            selected={data.timeTrackingDate}
            onChange={handleTimeTrackingDateChange}
            label={data.selectedTimeZoneName}
            calendarStartDay={userInfo?.firstDayOfWeek ?? 1}
          />
        </div>

        <div className={styles.timeWrapper}>
          <CustomTimePicker
            label="From"
            now={data.calendarNow}
            selected={data.startTime}
            onChange={handleStartTimeChange}
            isToday={isToday}
          />
          <CustomTimePicker
            label="To"
            now={data.calendarNow}
            selected={data.endTime}
            onChange={handleEndTimeChange}
            isToday={isToday}
            isNextDay={isNextDay}
            isFuture={isFuture || (isToday && isNextDay)}
          />
        </div>
      </div>

      <TimeZoneDifferenceBanner
        startTime={zonedTimeToUtc(data.startTime, data.selectedTimeZoneName)}
        endTime={zonedTimeToUtc(data.endTime, data.selectedTimeZoneName)}
        memberTimeZone={selectedMemberInfo.timeZone}
        selectedInTimeZoneFilterTimeZone={data.selectedTimeZoneName}
      />

      <div className={styles.billingWrapper}>
        <CustomCheckbox
          label="Billable"
          isChecked={data.isBillable}
          handleChange={toggleBillable}
        />
      </div>
      <TextArea
        label="Comments"
        value={data.notes}
        onChange={handleNotesChange}
        error={data.commentsLengthError}
        errorMessage={`*Maximum length ${MaximumLargeCommentLengthValidationError} characters`}
        style={{resize:"vertical"}}
      />
      <div className={styles.controlWrapper}>
        <Button onClick={handleClose}>Cancel</Button>

        {isJiraLoading ? (
          <div className={styles.JiraLoader}><PulseLoader/></div>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={disableSubmitCondition}
            >
              Send
            </Button>
          )
        }
      </div>
    </div>
  );
};

export default AddTimeForm;
